// Quit habits ("smoke-free day"): days free and money saved. Shared by backend, web and native.

export type QuitKind = 'smoke' | 'vape' | 'alcohol' | 'free'

export type QuitHabitInput = {
  title: string
  icon?: string | null
  quit?: boolean | null
}

const ICON_KIND: Record<string, QuitKind> = { 'cigarette-off': 'smoke', 'wine-off': 'alcohol' }

// Vape before smoke: "electronic cigarette" also names a cigarette.
const SUBJECTS: [QuitKind, RegExp][] = [
  ['vape', /\bvap(e|es|ing|ear|er|oter)\b|e-?cig|电子烟|電子タバコ|ベイプ/i],
  [
    'smoke',
    /smok|cigar|tobacco|nicotine|fumar|fumer|fumo|tabac|tabaco|タバコ|たばこ|煙草|喫煙|禁煙|烟/i,
  ],
  [
    'alcohol',
    /alcohol|álcool|alcool|booze|\bbeer|\bwine|\bdrink|\bsober|beber|bebida|cerveza|boire|休肝日|お酒|飲酒|禁酒|断酒|酒/i,
  ],
]

// Words that make a title about abstaining rather than doing.
const QUIT_WORDS =
  /\b(quit|quitting|stop|stopped|no|non|free|without|sober|sobriety|zero|cut|dejar|sin|parar|libre|sans|arr[eê]ter|pas de|ne pas|sem|largar|livre)\b|não|-free|ない|やめ|止め|禁|断|控え|休肝日|不|戒|无|停/i

// Titles that only ever mean abstaining, whatever else they say.
const ALWAYS_QUIT = /\bsober|sobriety|禁煙|禁酒|休肝日|断酒|戒烟|戒酒|不吸烟|不抽烟|不喝酒/i

function titleKind(title: string): QuitKind | null {
  return SUBJECTS.find(([, re]) => re.test(title))?.[0] ?? null
}

// Null when the habit isn't a quit habit. `quit` (the user's toggle) wins over detection.
export function quitKind(h: QuitHabitInput): QuitKind | null {
  if (h.quit === false) return null
  const title = h.title.split('///')[0]
  const fromTitle = titleKind(title)
  const fromIcon = h.icon ? (ICON_KIND[h.icon] ?? null) : null
  if (h.quit) return fromTitle ?? fromIcon ?? 'free'
  if (fromIcon) return fromTitle ?? fromIcon
  if (fromTitle && (ALWAYS_QUIT.test(title) || QUIT_WORDS.test(title))) return fromTitle
  return null
}

export const isQuitHabit = (h: QuitHabitInput) => quitKind(h) !== null

export const QUIT_COST_MAX = 1_000_000

export function cleanDailyCost(n: number | null | undefined): number | undefined {
  if (n == null || !Number.isFinite(n) || n <= 0) return undefined
  return Math.min(QUIT_COST_MAX, Math.round(n * 100) / 100)
}

export function moneySaved(freeDays: number, dailyCost: number | null | undefined) {
  return dailyCost ? Math.round(freeDays * dailyCost * 100) / 100 : null
}

// Region → currency for the cost input's default; anything unlisted falls back to USD.
const EURO = 'AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK'.split(' ')
const REGION_CURRENCY: Record<string, string> = {
  ...Object.fromEntries(EURO.map((r) => [r, 'EUR'])),
  JP: 'JPY',
  US: 'USD',
  GB: 'GBP',
  CN: 'CNY',
  TW: 'TWD',
  HK: 'HKD',
  SG: 'SGD',
  KR: 'KRW',
  IN: 'INR',
  CA: 'CAD',
  AU: 'AUD',
  NZ: 'NZD',
  CH: 'CHF',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
  CZ: 'CZK',
  BR: 'BRL',
  MX: 'MXN',
  AR: 'ARS',
  CL: 'CLP',
  CO: 'COP',
  PE: 'PEN',
  ZA: 'ZAR',
  PH: 'PHP',
  TH: 'THB',
  ID: 'IDR',
  MY: 'MYR',
  VN: 'VND',
  TR: 'TRY',
  IL: 'ILS',
  AE: 'AED',
  SA: 'SAR',
  NG: 'NGN',
  KE: 'KES',
  EG: 'EGP',
}

export const QUIT_CURRENCIES = [...new Set(Object.values(REGION_CURRENCY))].sort()

// IANA zone → region, so a Tokyo user on an en-US browser still gets yen.
const ZONE_REGION: Record<string, string> = Object.fromEntries(
  Object.entries({
    JP: 'Asia/Tokyo Japan',
    GB: 'Europe/London Europe/Belfast GB',
    IE: 'Europe/Dublin',
    FR: 'Europe/Paris',
    DE: 'Europe/Berlin',
    ES: 'Europe/Madrid Atlantic/Canary',
    IT: 'Europe/Rome',
    NL: 'Europe/Amsterdam',
    BE: 'Europe/Brussels',
    AT: 'Europe/Vienna',
    PT: 'Europe/Lisbon Atlantic/Madeira Atlantic/Azores',
    FI: 'Europe/Helsinki',
    GR: 'Europe/Athens',
    LU: 'Europe/Luxembourg',
    EE: 'Europe/Tallinn',
    LV: 'Europe/Riga',
    LT: 'Europe/Vilnius',
    SK: 'Europe/Bratislava',
    SI: 'Europe/Ljubljana',
    HR: 'Europe/Zagreb',
    MT: 'Europe/Malta',
    CY: 'Asia/Nicosia Europe/Nicosia',
    CH: 'Europe/Zurich',
    SE: 'Europe/Stockholm',
    NO: 'Europe/Oslo',
    DK: 'Europe/Copenhagen',
    PL: 'Europe/Warsaw',
    CZ: 'Europe/Prague',
    TR: 'Europe/Istanbul Asia/Istanbul Turkey',
    US: 'America/New_York America/Chicago America/Denver America/Los_Angeles America/Phoenix America/Anchorage America/Detroit America/Boise America/Juneau Pacific/Honolulu',
    CA: 'America/Toronto America/Vancouver America/Edmonton America/Winnipeg America/Halifax America/St_Johns America/Regina America/Montreal America/Moncton',
    MX: 'America/Mexico_City America/Cancun America/Monterrey America/Tijuana America/Merida America/Chihuahua America/Hermosillo America/Mazatlan',
    BR: 'America/Sao_Paulo America/Fortaleza America/Recife America/Bahia America/Manaus America/Belem',
    AR: 'America/Buenos_Aires',
    CL: 'America/Santiago',
    CO: 'America/Bogota',
    PE: 'America/Lima',
    NZ: 'Pacific/Auckland NZ',
    CN: 'Asia/Shanghai Asia/Urumqi PRC',
    HK: 'Asia/Hong_Kong',
    TW: 'Asia/Taipei',
    KR: 'Asia/Seoul',
    SG: 'Asia/Singapore',
    TH: 'Asia/Bangkok',
    ID: 'Asia/Jakarta Asia/Makassar Asia/Jayapura Asia/Pontianak',
    IN: 'Asia/Kolkata Asia/Calcutta',
    PH: 'Asia/Manila',
    VN: 'Asia/Ho_Chi_Minh Asia/Saigon',
    MY: 'Asia/Kuala_Lumpur Asia/Kuching',
    AE: 'Asia/Dubai',
    SA: 'Asia/Riyadh',
    IL: 'Asia/Jerusalem Asia/Tel_Aviv',
    ZA: 'Africa/Johannesburg',
    NG: 'Africa/Lagos',
    KE: 'Africa/Nairobi',
    EG: 'Africa/Cairo',
  }).flatMap(([region, zones]) => zones.split(' ').map((z) => [z, region])),
)

const ZONE_PREFIX_REGION: [string, string][] = [
  ['Australia/', 'AU'],
  ['America/Argentina/', 'AR'],
  ['America/Indiana/', 'US'],
  ['America/Kentucky/', 'US'],
  ['America/North_Dakota/', 'US'],
  ['US/', 'US'],
  ['Canada/', 'CA'],
]

export function currencyForTimeZone(timeZone: string | null | undefined): string | null {
  if (!timeZone) return null
  const region =
    ZONE_REGION[timeZone] ?? ZONE_PREFIX_REGION.find(([p]) => timeZone.startsWith(p))?.[1]
  return region ? (REGION_CURRENCY[region] ?? null) : null
}

export function regionOf(locale: string): string | null {
  try {
    return new Intl.Locale(locale).maximize().region ?? null
  } catch {
    return /[-_]([A-Z]{2})\b/.exec(locale)?.[1] ?? null
  }
}

// First locale that names a known region wins (e.g. ["en", "ja-JP"] on a Japanese Mac).
export function currencyForLocales(locales: readonly string[]): string {
  for (const l of locales) {
    if (!/[-_][A-Za-z]{2}\b/.test(l)) continue
    const c = REGION_CURRENCY[regionOf(l) ?? '']
    if (c) return c
  }
  for (const l of locales) {
    const c = REGION_CURRENCY[regionOf(l) ?? '']
    if (c) return c
  }
  return 'USD'
}

// The device's time zone says where the user is; the language often doesn't (en-US in Tokyo).
export function defaultCurrency(timeZone: string | null | undefined, locales: readonly string[]) {
  return currencyForTimeZone(timeZone) ?? currencyForLocales(locales)
}

export function formatMoney(amount: number, currency: string, locale: string): string {
  try {
    const whole = Number.isInteger(amount)
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...(whole && { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
    }).format(amount)
  } catch {
    return `${amount} ${currency}`
  }
}

export type QuitStats = {
  kind: QuitKind
  // Current run of free days (the habit's streak).
  days: number
  best: number
  // Every free day logged since tracking started; money saved counts these.
  freeDays: number
  dailyCost: number | null
  currency: string | null
}

const KIND_EN: Record<QuitKind, string> = {
  smoke: 'smoke-free',
  vape: 'vape-free',
  alcohol: 'alcohol-free',
  free: 'free',
}

// English line for coach prompts: "23 days smoke-free (best 41), 60 free days total, ¥36,000 saved".
export function quitText(q: QuitStats): string {
  const saved = moneySaved(q.freeDays, q.dailyCost)
  return [
    `${q.days} ${q.days === 1 ? 'day' : 'days'} ${KIND_EN[q.kind]}${q.best > q.days ? ` (best ${q.best})` : ''}`,
    `${q.freeDays} free ${q.freeDays === 1 ? 'day' : 'days'} total`,
    saved !== null && q.currency && `${formatMoney(saved, q.currency, 'en')} saved`,
  ]
    .filter(Boolean)
    .join(', ')
}
